import { ref } from "vue";
import { extractApiError } from "@/utils/errorHandler";

interface HttpErrorEntry {
  id: string;
  error: {
    httpStatusCode?: number;
    defaultUserMessage?: string;
  };
  timestamp: Date;
  source: string;
  url: string;
  method: string;
}

// Global error state
const errors = ref<HttpErrorEntry[]>([]);
const lastSuccessfulRequestToken = ref(0);

/**
 * Add an error to the global error list
 * @param {Object} error - The error object from HTTP request
 * @param {Object} options - Options for error handling
 */
interface AddHttpErrorOptions {
  source?: string;
}

export const addHttpError = (
  error: {
    response?: { status?: number };
    config?: { url?: string; method?: string };
    request?: { responseURL?: string };
  },
  options: AddHttpErrorOptions = {},
) => {
  // Skip 401 errors as they're handled by the auth interceptor
  if (error?.response?.status === 401) {
    return;
  }

  // Extract structured error
  const extractedError = extractApiError(error);

  const url = error?.config?.url || error?.request?.responseURL || "Unknown";
  const method = error?.config?.method?.toUpperCase() || "UNKNOWN";

  // Check if similar error already exists (avoid exact duplicates within last 2 seconds)
  const now = Date.now();
  const twoSecondsAgo = now - 2000;
  const existingError = errors.value.find((e) => {
    const isSameError =
      e.error.httpStatusCode === extractedError.httpStatusCode &&
      e.error.defaultUserMessage === extractedError.defaultUserMessage &&
      e.url === url &&
      e.method === method;
    const isRecent = e.timestamp.getTime() > twoSecondsAgo;
    return isSameError && isRecent;
  });

  if (existingError) {
    return; // Skip duplicate error
  }

  // Create error entry with ID and timestamp
  const errorId = `${method}-${url}-${extractedError.httpStatusCode}-${now}`;
  const errorEntry = {
    id: errorId,
    error: extractedError,
    timestamp: new Date(),
    source: options.source || "HTTP Request",
    url: url,
    method: method,
  };

  // Replace with new error (keep only the latest)
  errors.value = [errorEntry];
};

/**
 * Remove an error by ID
 * @param {string} errorId - The error ID to remove
 */
export const removeHttpError = (errorId: string) => {
  const index = errors.value.findIndex((e) => e.id === errorId);
  if (index > -1) {
    errors.value.splice(index, 1);
  }
};

/**
 * Clear all errors
 */
export const clearAllHttpErrors = () => {
  errors.value = [];
};

/**
 * Register a successful HTTP request to allow UI side-effects (like clearing toasts)
 */
export const registerHttpSuccess = () => {
  lastSuccessfulRequestToken.value = Date.now();
};

/**
 * Clear errors older than specified minutes
 * @param {number} minutes - Minutes of age threshold
 */
export const clearOldHttpErrors = (minutes = 5): void => {
  const threshold = Date.now() - minutes * 60 * 1000;
  errors.value = errors.value.filter((e) => e.timestamp.getTime() > threshold);
};

/**
 * Composable for using HTTP errors in components
 */
export const useHttpErrors = () => {
  return {
    errors,
    addHttpError,
    removeHttpError,
    clearAllHttpErrors,
    clearOldHttpErrors,
    lastSuccessfulRequestToken,
  };
};
